import React from 'react'
import { connect } from 'react-redux'
import Loader from 'components/D3Loader'
import { getUserRoleByStudy, getUserScopeByStudy, hasPiiRole, disableScope, getUserRoleIdByStudy } from 'utils/misc'
import { actions as tableFilterActions } from 'store/tableFilters'
import { modalActions } from 'store/modal'
import { resetDocs } from 'store/documents'
import { drawerActions } from 'store/drawer'
import { USER_ROLES_MAP, USER_SCOPES, USER_ROLE_IDS_WITH_LIMITED_SIDEBAR_ACCESS } from 'utils/constants'
import { checkIsEmailless } from 'utils/studyConfigUtils'
import SadCloud from 'components/SadCloud'
import { actions as ptpsActions, ptpInBlockedConsentAlert } from '../modules/Participants'
import { actions as createPtpActions } from '../../CreateParticipant/modules/CreateParticipant'
import ParticipantWizard from '../components/ParticipantWizard'
import ParticipantsPage from '../components/ParticipantsPage'
import { onSetOrphanedParticipants } from '../../../../Sites/routes/SitesPage/modules/Sites'
import { isVersionUatOrLive, getStudyVersionStatus } from '../../../../../../../../../utils/studyConfigUtils'

const mapStateToProps = (state, ownProps) => {
  const { studyVersionID } = ownProps?.params || {}
  const { loading, participants, searchTerm, sites, studies, study, user, studyLock, consents } = state
  const { leafSites, sitesList } = sites
  const { currentStudy, studyVersionList } = study

  const studyID = currentStudy.id
  const studyName = currentStudy.platform_name
  const currentUserRole = getUserRoleByStudy(user, studyID)
  const currentRoleId = getUserRoleIdByStudy(user, studyID)
  const userScope = getUserScopeByStudy(user, currentStudy)
  const participantDataFields = currentStudy.config?.participant_data_fields
  const caregiverDataFields = currentStudy.config?.caregiver_data_fields
  const isEmailless = checkIsEmailless(currentStudy)
  const noTracksFeature = currentStudy.config?.tracks?.disabled === true

  const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
  const hasCaregiverSupport = currentStudy.config?.caregivers?.version === 2 && hasStudyPromotion
  const isReadOnlyState = hasStudyPromotion && isVersionUatOrLive(studyVersionList, studyVersionID)
  let isStudyVersionInSiteList = leafSites && leafSites.length > 0
  if (hasStudyPromotion) {
    const siteVersionStatuses = sitesList
      .map(inner => inner.find(item => item.versionStatus)?.versionStatus)
      .filter(Boolean) // Removes undefined/null
      .map(status => status.toLowerCase())
    const studyVersionLower = getStudyVersionStatus(studyVersionList, studyVersionID)
    // Check if current sites exist in current study version status
    isStudyVersionInSiteList = siteVersionStatuses.includes(studyVersionLower)
  }
  return {
    ...participants,
    canAddPtp: userScope.includes('wxp'),
    canAddCohort: userScope.includes('wxc'),
    canDelete: userScope.includes('dp'),
    canMovePtp: userScope.includes('mp'),
    canResendInviteOrResetPw: userScope.includes('rpe'),
    canSeeOrphans: [
      USER_ROLES_MAP.root,
      USER_ROLES_MAP.studyAdmin,
      USER_ROLES_MAP.studyCoordinator,
      USER_ROLES_MAP.investigator,
    ].includes(currentUserRole),
    canWrite: userScope.includes('wp'),
    consentRequired: currentStudy.consent_required,
    disableTrackCreation: disableScope({ study, user, scope: USER_SCOPES.wxc.code }),
    filterProps: state.tableFilters,
    isCaseManager: currentUserRole === USER_ROLES_MAP.caseManager,
    isClinOrSiteCoordinator: USER_ROLE_IDS_WITH_LIMITED_SIDEBAR_ACCESS.includes(currentRoleId),
    isEmailless,
    leafSites,
    loading: loading.participant,
    noTracksFeature,
    caregiversList: Object.values(participants?.caregivers?.caregivers),
    participantList: Object.values(participants?.participants),
    ptpsComplianceLoading: loading.ptpsCompliance,
    ptpMoveLoading: loading.moveParticipants,
    page: 'tracks' in state.location.query ? 'tracks' : 'participants',
    searchTerm,
    isStudyVersionInSiteList,
    studyID,
    studyName,
    studies,
    wizardName: state.participants.wizardName,
    hasPiiRole: hasPiiRole(currentUserRole),
    studyLock,
    participantDataFields,
    caregiverDataFields,
    hasCaregiverSupport,
    study: currentStudy,
    unCompleteBlockedConsents: consents?.unCompletedBlockedConsents || {},
    isReadOnlyState,
    studyVersionID,
  }
}

const mapDispatchToProps = {
  resetDocs,
  ...ptpsActions,
  ...tableFilterActions,
  ...modalActions,
  ...drawerActions,
  onSetOrphanedParticipants,
  resetParticipant: createPtpActions.resetParticipant,
  triggerResendInvite: createPtpActions.triggerResendInvite,
  ptpInBlockedConsentAlert,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(props => {
  const { canAddPtp, participantList, loading } = props
  const hasNoParticipants = participantList.length === 0

  if (loading) {
    return <Loader />
  }

  if (hasNoParticipants) {
    return canAddPtp ? (
      <ParticipantWizard {...props} />
    ) : (
      <SadCloud text='There are 0 participants. Please check back later.' />
    )
  }

  return <ParticipantsPage {...props} />
})
